<template>
  <div
    ref="mapRef"
    class="map"
    @click="(event) => emitMarkerInteraction(event, 'select')"
    @mouseover="(event) => emitMarkerInteraction(event, 'mouseover')"
    @mouseout="(event) => emitMarkerInteraction(event, 'mouseout')"
  />
</template>

<script setup>
  import { onMounted, useTemplateRef, watch } from 'vue'

  import useGoogleMap from '@shared/components/CareProvider/useGoogleMap'
  import useI18n from '@shared/hooks/useI18n'

  // HOOKS
  const { debouncedPanToSwissBounds, drawMarkers, initGoogleMap, isOutsideOfSwitzerland, mapBoundsToCoordinates, updateInfoWindow } = useGoogleMap()
  const { t } = useI18n()

  // INIT
  const emit = defineEmits(['mouseover', 'mouseout', 'search', 'select'])
  const props = defineProps({
    avmNetwork: {
      type: String,
      required: true,
    },

    hasOnlyAlternativeResults: {
      type: Boolean,
      default: false,
    },

    hovered: {
      type: [String, null],
      default: null,
    },

    location: {
      type: [Object, null],
      default: () => {}
    },

    results: {
      type: Array,
      default: () => []
    },

    selected: {
      type: [String, null],
      default: null,
    },

    zoomControl: {
      type: Boolean,
      default: true,
    }
  })

  // DATA
  const mapRef = useTemplateRef('mapRef')

  // METHODS
  function emitMarkerInteraction(event, emitType) {
    if(event.target.tagName !== 'svg' || !event.target.classList.contains('custom-marker')) {
      event.stopPropagation()
      return
    }

    const zsrNumber = event.target.dataset.zsrNumber
    emit(emitType, zsrNumber)
  }

  async function init() {

    const { map } = await initGoogleMap({
      location: props.location,
      mapRef: mapRef.value,
      zoomControl: props.zoomControl
    })

    initListeners(map)
  }

  function initListeners(map) {
    map.addListener('tilesloaded', () => {
      if (isOutsideOfSwitzerland()) return
      const bounds = map.getBounds()
      emit('search', mapBoundsToCoordinates(bounds))
    })

    map.addListener('bounds_changed', () => {
      debouncedPanToSwissBounds()
    })
  }

  function updateMarkerInfoWindow(zsrNumber, isActive) {
    const careProvider = zsrNumber ? props.results.find(result => result.zsrNumber === zsrNumber) : null
    const buttonLabel = props.hasOnlyAlternativeResults
      ? t(`careProvider.result.card.${props.avmNetwork}.switch`)
      : t('careProvider.add')
    updateInfoWindow({
      buttonLabel,
      careProvider,
      isActive
    })
  }

  // WATCHER
  watch(
    () => props.results,
    (val) => {
      const markerData = val.map(result => {
        const point = result.coordinates
        const zsrNumber = result.zsrNumber
        return { point, zsrNumber }
      })
      drawMarkers(markerData)
    },
    { deep: true }
  )

  watch(
    () => props.hovered,
    (val) => updateMarkerInfoWindow(val, false)
  )

  watch(
    () => props.selected,
    (val) => updateMarkerInfoWindow(val, true)
  )

  // LIFECYCLE HOOKS
  onMounted(async () => {
    await init()
  })
</script>

<style scoped>
.map {
  height: 100vh;
  width: 100%;
}

.map:deep(.gm-style-iw-c) {
  font-family: var(--font-family-open-sans);
  font-size: 16px;
  padding: var(--fixed-spacing-fix-06) !important;
  background-color: var(--surface);
  border-radius: var(--fixed-border-radius-fix-02);
  box-shadow: var(--elevation-level-2);
}

.map:deep(.gm-style-iw-chr) {
  display: none;
}

.map:deep(.gm-style-iw-d) {
  overflow: auto !important;
  padding: 0;
}

.map:deep(.custom-marker) {
  height: 36px;
  cursor: pointer;
}

.map:deep(.custom-marker .inner) {
  fill: var(--surface);
  pointer-events: none;
  transition: fill 0.15s ease-in;
}

.map:deep(.custom-marker .border) {
  fill: var(--on-surface);
  pointer-events: none;
  transition: fill 0.15s ease-in;
}

.map:deep(.custom-marker--hovered:not(.custom-marker--selected) .border) {
  fill: var(--primary);
}

.map:deep(.custom-marker--selected .inner) {
  fill: var(--primary);
}

.map:deep(.custom-marker--selected .border) {
  fill: var(--primary);
}
</style>
